'use client';

import { throttle } from 'lodash';
import { useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import { ClassNameValue } from 'tailwind-merge';

import { styles } from './UiModHeadFixed.styles';

type NativeElement = React.HTMLAttributes<HTMLDivElement>;

export const CSS_PROPERTY_NAME = '--head-fixed-height';
const Z_INDEX = 50;
const COORDINATE_TOLERANCE = 1; // пикселей - допуск для сравнения координат на мобильных устройствах
const IOS_COORDINATE_TOLERANCE = 15; // увеличенный допуск для iOS bounce scrolling
const STABILITY_DELAY = 100; // мс - задержка для стабилизации состояния на iOS

// Детекция iOS
const isIOS = () => {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window)
  );
};

// Детекция bounce scrolling на iOS
const isBounceScrolling = () => {
  if (!isIOS()) {
    return false;
  }

  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const maxScrollTop =
    document.documentElement.scrollHeight - window.innerHeight;

  // Bounce scrolling происходит когда scrollTop < 0 или > maxScrollTop
  return scrollTop < 0 || scrollTop > maxScrollTop;
};

export interface UiModHeadFixedProps {
  className?: ClassNameValue;
  children?: React.ReactNode;
  onClick?: NativeElement['onClick'];
  ref?: React.RefObject<HTMLDivElement | null>;
  onFixedChange?: (isFixed: boolean) => void;
  /** Показывать ли элемент (управляется извне) */
  show?: boolean;
  /** Тип позиционирования: sticky для многоуровневой системы, fixed для независимого элемента */
  positioning?: 'sticky' | 'fixed';
  order: number;
}

const list: Record<
  string,
  {
    order: number;
    bottom: number;
    isHidden: boolean;
    isFixed: boolean;
  }
> = {};

const updateShadows = () => {
  // Находим последний fixed элемент для тени
  const allFixed = Object.values(list)
    .filter((item) => !item.isHidden && item.isFixed)
    .sort((a, b) => b.order - a.order);

  const lastItem = allFixed[0];

  // Проходим по ВСЕМ элементам для корректной установки теней
  Object.keys(list).forEach((itemId) => {
    const element = document.querySelector(
      `[data-head-fixed-id="${itemId}"]`
    ) as HTMLElement;
    if (element) {
      const item = list[itemId];
      // Тень только у fixed элементов, и только у последнего
      const shouldHaveShadow = item.isFixed && lastItem && item === lastItem;
      element.style.boxShadow = shouldHaveShadow
        ? 'var(--shadow-header)'
        : 'none';
    }
  });
};

const getPrevBottom = (id: string) => {
  const order = list[id].order;
  const items = Object.values(list)
    .filter((item) => !item.isHidden)
    // Убираем фильтр по isFixed - учитываем ВСЕ видимые элементы с меньшим order
    .filter((item) => item.order < order)
    .sort((a, b) => a.order - b.order); // Сортируем по order для корректного порядка

  // Аккумулируем высоты всех предыдущих элементов
  return items.reduce(
    (totalHeight, item) => totalHeight + (item.bottom || 0),
    0
  );
};

const getLastFixedForHeight = () => {
  const items = Object.values(list)
    .filter((item) => !item.isHidden)
    .filter((item) => item.isFixed);
  if (items.length === 0) return null;
  return items[items.length - 1];
};

// Синхронное обновление позиций всех элементов для предотвращения наложения
const updateAllElementsPositions = throttle(() => {
  // Получаем все элементы отсортированные по order
  const sortedItems = Object.entries(list).sort(
    ([, a], [, b]) => a.order - b.order
  );

  sortedItems.forEach(([itemId, item]) => {
    const element = document.querySelector(
      `[data-head-fixed-id="${itemId}"]`
    ) as HTMLElement;
    if (!element || item.isHidden) return;

    // Сохраняем высоту элемента (не координаты viewport!)
    const elementHeight = element.offsetHeight;
    item.bottom = elementHeight;

    // Рассчитываем правильную позицию top
    const prevBottom = getPrevBottom(itemId);

    // Устанавливаем позицию для всех элементов
    element.style.top = `${prevBottom}px`;
    element.style.zIndex = `${Z_INDEX - item.order * 5}`;

    const top = element.getBoundingClientRect().top;

    // Обновляем CSS переменные с высотой элемента
    document.documentElement.style.setProperty(
      `${CSS_PROPERTY_NAME}-${item.order}`,
      `${top + elementHeight}px`
    );
  });

  // Обновляем общую высоту (сумма высот всех fixed элементов)
  const fixedItems = Object.values(list).filter(
    (item) => !item.isHidden && item.isFixed
  );

  if (fixedItems.length > 0) {
    const totalFixedHeight = fixedItems.reduce(
      (sum, item) => sum + (item.bottom || 0),
      0
    );
    document.documentElement.style.setProperty(
      CSS_PROPERTY_NAME,
      `${totalFixedHeight}px`
    );
  }

  // Обновляем тени
  updateShadows();
}, 16); // 60fps throttling

/**
 * Компонент для фиксации шапки сайта при скролле
 * Многоуровневая фиксация, когда несколько шапок могут быть фиксированы одновременно
 * Добавляет и вычисляет top, z-index и box-shadow(последнему фиксированному элементу)
 */
export function UiModHeadFixed({
  className,
  children,
  onFixedChange,
  show = true,
  positioning = 'sticky',
  order,
  ref: externalRef,
  ...props
}: UiModHeadFixedProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [fixed, setFixed] = useState(false);
  const id = useId();

  const slots = styles({ fixed, positioning });

  // iOS стабилизация состояния
  const stabilityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastStableStateRef = useRef<boolean>(false);

  const checkFixed = () => {
    if (!ref.current) {
      return;
    }

    // Логика для fixed элементов
    if (positioning === 'fixed') {
      const prevBottom = getPrevBottom(id);

      // Обновляем стили напрямую через ref
      ref.current.style.top = `${prevBottom}px`;

      const elementHeight = ref.current.offsetHeight;
      list[id].bottom = elementHeight;

      // Обновляем CSS переменные с высотой элемента
      document.documentElement.style.setProperty(
        `${CSS_PROPERTY_NAME}-${order}`,
        `${elementHeight}px`
      );

      // Обновляем общую высоту (сумма высот всех fixed элементов)
      const lastFixedForHeight = getLastFixedForHeight();
      if (lastFixedForHeight) {
        const totalFixedHeight = Object.values(list)
          .filter((item) => !item.isHidden && item.isFixed)
          .reduce((sum, item) => sum + (item.bottom || 0), 0);

        document.documentElement.style.setProperty(
          CSS_PROPERTY_NAME,
          `${totalFixedHeight}px`
        );
      }

      // Синхронно обновляем позиции ВСЕХ элементов (включая z-index)
      updateAllElementsPositions();
      updateShadows();
      setFixed(true);
      onFixedChange?.(true);
      return;
    }

    // Логика для sticky элементов с синхронным обновлением
    const computedStyle = getComputedStyle(ref.current);
    const hidden =
      computedStyle.display === 'none' ||
      computedStyle.visibility === 'hidden' ||
      ref.current.offsetParent === null;
    const sticky = computedStyle.position === 'sticky';

    list[id].isHidden = hidden || !sticky;
    if (hidden || !sticky) {
      return;
    }

    const { top } = ref.current.getBoundingClientRect();
    const prevBottom = getPrevBottom(id);

    // Используем разный допуск для iOS и других устройств
    const tolerance = isIOS() ? IOS_COORDINATE_TOLERANCE : COORDINATE_TOLERANCE;
    const isFixedCalculated = Math.abs(top - prevBottom) <= tolerance;

    // Игнорируем изменения во время bounce scrolling на iOS
    if (isIOS() && isBounceScrolling()) {
      // Обновляем позиции но не меняем состояние isFixed
      updateAllElementsPositions();
      return;
    }

    const wasFixed = list[id].isFixed;
    list[id].isFixed = isFixedCalculated;

    // Стабилизация состояния для iOS
    if (isIOS() && wasFixed !== isFixedCalculated) {
      // Отменяем предыдущий таймер если есть
      if (stabilityTimerRef.current) {
        clearTimeout(stabilityTimerRef.current);
      }

      // Устанавливаем новый таймер для стабилизации
      stabilityTimerRef.current = setTimeout(() => {
        // Проверяем что состояние стабильно
        if (list[id] && list[id].isFixed === isFixedCalculated) {
          setFixed(isFixedCalculated);
          onFixedChange?.(isFixedCalculated);
          lastStableStateRef.current = isFixedCalculated;
        }
        stabilityTimerRef.current = null;
      }, STABILITY_DELAY);
    } else if (!isIOS() && wasFixed !== isFixedCalculated) {
      // Для не-iOS устройств обновляем сразу
      setFixed(isFixedCalculated);
      onFixedChange?.(isFixedCalculated);
    }

    // Синхронно обновляем позиции ВСЕХ элементов
    updateAllElementsPositions();
  };

  useEffect(() => {
    if (show) {
      list[id] = {
        order,
        bottom: 0,
        isHidden: false,
        isFixed: positioning === 'fixed' // для fixed сразу помечаем как зафиксированный
      };

      setTimeout(() => {
        updateAllElementsPositions();
        checkFixed();
      }, 300);

      const throttledCheckFixed = throttle(checkFixed, 16);

      // Добавляем обработчики только для sticky элементов
      if (positioning === 'sticky') {
        window.addEventListener('scroll', throttledCheckFixed);
        window.addEventListener('resize', throttledCheckFixed);
        // Обработка изменения ориентации экрана на мобильных устройствах
        window.addEventListener('orientationchange', throttledCheckFixed);
        window.addEventListener('DOMContentLoaded', throttledCheckFixed);
      }

      // Для fixed элементов добавляем ResizeObserver
      let resizeObserver: ResizeObserver | null = null;
      if (positioning === 'fixed' && ref.current) {
        const throttledResize = throttle(checkFixed, 100);
        resizeObserver = new ResizeObserver(throttledResize);
        resizeObserver.observe(ref.current);
      }

      return () => {
        // Очищаем таймер стабилизации для iOS
        if (stabilityTimerRef.current) {
          clearTimeout(stabilityTimerRef.current);
          stabilityTimerRef.current = null;
        }

        // Сохраняем состояние для восстановления теней
        const wasLast =
          list[id] &&
          Object.values(list)
            .filter((item) => !item.isHidden && item.isFixed)
            .sort((a, b) => b.order - a.order)[0] === list[id];

        delete list?.[id];

        // Восстанавливаем тени если удаляемый был последним
        if (wasLast && Object.keys(list).length > 0) {
          updateShadows();
        }

        window.removeEventListener('scroll', throttledCheckFixed);
        window.removeEventListener('resize', throttledCheckFixed);
        window.removeEventListener('orientationchange', throttledCheckFixed);

        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    } else {
      // Сбрасываем состояние когда скрыто
      setFixed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useImperativeHandle(externalRef, () => ref.current as HTMLDivElement);

  if (!show) {
    return null;
  }

  return (
    <div
      className={slots.base({ className })}
      ref={ref}
      data-head-fixed-id={id}
      {...props}
    >
      {children}
    </div>
  );
}
